import { DictType } from "@pma/types";
import { RECAPTCHA_ENDPOINT } from "@pma/utils/api-routes";
import { Dispatch, SetStateAction, Suspense, useEffect, useMemo } from "react";
import type { ReCAPTCHAProps } from "react-google-recaptcha";
import ReCAPTCHABase from "react-google-recaptcha";

const ReCAPTCHA = ReCAPTCHABase as unknown as React.ComponentType<ReCAPTCHAProps>;
import { useLocation } from "react-router-dom";
import PmsLoader from "@components/common/loader";
import useTranslator from "@hooks/use-translator";
import { cn } from "@pma/utils/helpers";

interface Props {
  setToken: Dispatch<SetStateAction<DictType>>;
  v2?: boolean;
  reSubmit: CallableFunction;
  className?:string
}

const Recaptcha = ({ setToken, v2, reSubmit ,className= "fixed bottom-3 transition-all ease-in-out"}: Props) => {
  const ReCAPTCHA_V3_SITE_KEY = process.env.REACT_APP_ReCAPTCHA_V3_SITE_KEY!;
  const ReCAPTCHA_V2_SITE_KEY = process.env.REACT_APP_ReCAPTCHA_V2_SITE_KEY!;

  const { locale } = useTranslator();

  const isHebrew = locale === "he";

  const handleLoadedV3 = (_: any) => {
    ReCAPTCHA_V3_SITE_KEY &&
      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(ReCAPTCHA_V3_SITE_KEY, { action: "login" })
          .then((token: any) => {
            setToken((prev) => ({ ...prev, v3: token }));
          });
      });
  };

  const handleV2OnChange = (token: string | null) => {
    setToken((prev) => ({ ...prev, v2: token }));
    reSubmit(token);
  };

  useEffect(() => {
    const script = document.createElement("script");
    script.src = RECAPTCHA_ENDPOINT + `?render=${ReCAPTCHA_V3_SITE_KEY}`;
    script.defer = true;
    script.async = true;
    script.addEventListener("load", handleLoadedV3);
    document.body.appendChild(script);
  }, []);

  if (v2) {
    return (
      <Suspense fallback={<PmsLoader />}>
        <div
          className={cn(
            className,
            isHebrew ? "left-0" : "right-0",
          )}
        >
          <ReCAPTCHA
            sitekey={ReCAPTCHA_V2_SITE_KEY}
            onChange={handleV2OnChange}
          />
        </div>
      </Suspense>
    );
  }

  return <></>;
};

export default Recaptcha;

export const RecaptchaWrapper = ({ children }: any) => {
  const ReCAPTCHA_V3_SITE_KEY = process.env.REACT_APP_ReCAPTCHA_V3_SITE_KEY!;

  const recaptchaPages = ["/login", "/reset-password"];
  const activePath = useLocation().pathname;

  const path = useMemo(() => activePath?.replace("/", ""), [activePath]);

  useEffect(() => {
    const styleElement = document.createElement("style");

    // Hide the recaptcha icon from DOM if the page doesn't require recaptcha
    if (!recaptchaPages.includes(path)) {
      styleElement.innerHTML = `
      .grecaptcha-badge {
        display: none !important;
      }
      `;
      document.head.appendChild(styleElement);
    } else {
    }

    return () => {
      !recaptchaPages.includes(path) && document.head.removeChild(styleElement);
    };
  }, [path]);

  // V3
  return (
    <Suspense fallback={<PmsLoader />}>
      {children}
      <div
        className="g-recaptcha"
        data-sitekey={ReCAPTCHA_V3_SITE_KEY}
        data-size="invisible"
      ></div>
    </Suspense>
  );
};
